import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  deleteFleetsUuidRemoveDevices,
  GET_DEVICE_RESPONSE,
  getDevice,
  postFleetsUuidAddDevices,
} from "../../../../api"
import { config } from "../../../../constants"

type TTracker = GET_DEVICE_RESPONSE["devices"][0]

export const useTrackerSelectList = (
  partnerUuid: string,
  fleetUuid: string,
  mode: "include" | "exclude",
): {
  searchText: string
  activePage: number
  totalPages: number
  trackerCount: number
  trackerList: TTracker[]
  loadingItemIds: string[]
  refreshLoading: boolean
  onSearchChange: (text: string) => void
  onPageChange: (page: number) => void
  onItemPress: (
    updateFn: () => void,
  ) => (serialnumber: string, trackerCurrentlyInFleet: { uuid: string; name: string }) => void
  refresh: () => Promise<void>
} => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState<string>("")
  const [activePage, setActivePage] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [trackerCount, setTrackerCount] = useState<number>(0)
  const [trackerList, setTrackerList] = useState<TTracker[]>([])
  const [loadingItemIds, setLoadingItemIds] = useState<string[]>([])
  const [refreshLoading, setRefreshLoading] = useState<boolean>(false)

  const refresh = useCallback(async () => {
    setRefreshLoading(true)
    const { response, data } = await getDevice(partnerUuid, undefined, {
      search_string: searchText,
      page: activePage,
      per_page: 10,
      ...(mode === "include" ? { fleet_uuid: [fleetUuid] } : {}),
      ...(mode === "exclude" ? { exclude_fleet_uuid: [fleetUuid] } : {}),
    })
    if (response.ok) {
      setTrackerList(data.devices)
      setTotalPages(Math.ceil((data.meta.total || 0) / (data.meta.per_page || config.itemsPerPage)))
      setTrackerCount(data.meta.total || 0)
    }
    setRefreshLoading(false)
  }, [searchText, activePage, mode, fleetUuid, partnerUuid])

  useEffect(() => {
    refresh()
  }, [refresh])

  const onSearchChange = (text: string) => {
    setActivePage(0)
    setSearchText(text)
  }

  const onPageChange = (page: number) => {
    setActivePage(page)
  }

  const onItemPress =
    (callback: () => void) =>
    async (serialnumber: string, trackerCurrentlyInFleet: { uuid: string; name: string }) => {
      if (mode === "include") {
        setLoadingItemIds((state) => [...state, serialnumber])
        await deleteFleetsUuidRemoveDevices(partnerUuid, fleetUuid, {
          device_serialnumbers: [serialnumber],
        })
        setLoadingItemIds((state) => state.filter((id) => id !== serialnumber))
        refresh()
        callback()
      } else {
        setLoadingItemIds((state) => [...state, serialnumber])

        if (trackerCurrentlyInFleet.uuid) {
          // Add confirmation prompt when tracker is already in another fleet
          const isConfirmed = window.confirm(
            t("settings.partner.tracker_move_confirmation", {
              fleetName: trackerCurrentlyInFleet.name,
            }),
          )

          if (!isConfirmed) {
            setLoadingItemIds((state) => state.filter((id) => id !== serialnumber))
            return
          }

          await deleteFleetsUuidRemoveDevices(partnerUuid, trackerCurrentlyInFleet.uuid, {
            device_serialnumbers: [serialnumber],
          })
        }

        await postFleetsUuidAddDevices(partnerUuid, fleetUuid, {
          device_serialnumbers: [serialnumber],
        })
        setLoadingItemIds((state) => state.filter((id) => id !== serialnumber))
        refresh()
        callback()
      }
    }

  return {
    searchText,
    activePage,
    totalPages,
    trackerCount,
    trackerList,
    loadingItemIds,
    refreshLoading,
    onSearchChange,
    onPageChange,
    onItemPress,
    refresh,
  }
}
